import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { Box, FormHelperText, Grid, Hidden, makeStyles, Theme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import gray from '@material-ui/core/colors/grey';
import { FormFieldProps } from '../FormFieldDisplay';
import { DropDownField } from '../../../../store/modules/application/types';
import { isArray, isObject, forEach, orderBy } from 'lodash';
import { getRequiredScopes } from '../../../../authentication';
import { Api } from '../../../../store/services/services';
import { useAuthenticationContext } from '../../../../authentication';
import { composeParams } from '../../../../utils/query';
import AutoCompleteSelect from '../../../AutoCompleteSelect';

const useStyles = makeStyles((theme: Theme) => ({
    inputCustomStyle: {
        fontSize: 13,
    },
    detailNodeLabelSm: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'left',
        justifyContent: 'right',
        marginRight: 30,
        lineHeight: '21px',
    },
    detailNodeLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'right',
        justifyContent: 'right',
        marginRight: 30,
        lineHeight: '21px',
    },
    detailNodeValue: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        lineHeight: '21px',
        wordWrap: 'break-word',
        marginBottom: '5px',
        width: '100%',
    },
    borderedInputs: {
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
    },
    disabled: {
        color: '#484848 !important',
    },
    width: {
        width: '100%',
    },
}));

const MultiSelectDropDownFormField: FunctionComponent<FormFieldProps> = (props) => {
    const { formField, value, error, readOnly, handleInputChange, formData, isCustomStyle } = props;
    const authContext = useAuthenticationContext();

    const [choices, setChoices] = useState<DropDownField[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    let selectedOptions: Array<string> = [];
    let selectedLabels: string[] = [''];
    const classes = useStyles();
    const { scopes } = authContext;

    if (value) {
        if (isArray(value)) {
            selectedLabels = value
                .flatMap((item: string) => {
                    return choices.filter((choice: DropDownField) => {
                        return item === choice.value;
                    });
                })
                .map((option: DropDownField) => option.label);
            selectedOptions = value;
        } else {
            selectedOptions.push(value);
            handleInputChange(formField.field, selectedOptions);
        }
    }

    const getFieldChoices = useCallback(async () => {
        let choices: DropDownField[] = [];

        if (formField.api) {
            let url: string = formField.api.url;
            if (formField.api.queryParams) {
                const queryParams = composeParams(formField.api.queryParams, formData);

                url = `${url}?${queryParams}`;
            }

            const scope = getRequiredScopes(url);

            if (scope) {
                const token = await authContext
                    .getAccessToken({ scopes: [scopes[scope]] })
                    .then((response) => response.accessToken);

                setLoading(true);
                let data = await Api.getChoices(url, token);
                setLoading(false);

                if (isArray(data)) {
                    if (formField.api.resultMapping) {
                        choices = data.map((choice) => {
                            let choiceMap: any = {};

                            forEach(formField?.api?.resultMapping!, (mapValue, mapKey) => {
                                choiceMap[mapKey] = choice[mapValue];
                            });

                            return choiceMap;
                        });
                    } else if (data.some((choice) => !isObject(choice))) {
                        choices = data.map((choice) => {
                            return {
                                label: choice,
                                value: choice,
                            };
                        });
                    } else {
                        choices = data;
                    }
                }

                if (choices) {
                    setChoices(orderBy(choices, 'label', 'asc'));
                }
            }
        } else {
            setChoices(orderBy(formField.choices, 'label', 'asc'));
        }

        // eslint-disable-next-line
    }, [formField, scopes]);

    useEffect(() => {
        getFieldChoices();
    }, [getFieldChoices]);

    const selectedValues = choices?.filter((choice: DropDownField) => value.some((val: any) => val === choice.value));

    const onHandleInputChange = (field: string, data: DropDownField[]) => {
        const selectedValues = data.map((obj) => obj.value);
        handleInputChange(field, selectedValues);
    };

    const Loader = formField.api && <Skeleton variant="text" width="100%" />;
    return (
        <Grid container alignItems={readOnly ? 'flex-start' : 'center'}>
            <Grid item xs={isCustomStyle ? 3 : 6}>
                <Hidden smUp={true}>
                    <Box component="div" className={classes.detailNodeLabelSm}>
                        {formField.label}{' '}
                    </Box>
                </Hidden>
                <Hidden xsDown={true}>
                    <Box component="div" className={classes.detailNodeLabel}>
                        {formField.label}{' '}
                    </Box>
                </Hidden>
            </Grid>
            <Grid item xs={isCustomStyle ? 9 : 6}>
                <Box component="div" className={classes.detailNodeValue}>
                    {!readOnly && (
                        <>
                            <AutoCompleteSelect
                                data={choices}
                                inputSize={'small'}
                                variant={'outlined'}
                                error={error?.error}
                                isMultipleValue={true}
                                disableCloseOnSelect={true}
                                disableClearable={true}
                                value={selectedValues}
                                ariaLabel={formField.label}
                                onChange={(data: DropDownField[]) => onHandleInputChange(formField.field, data)}
                            />
                            {error?.messages?.map((msg, index) => (
                                <FormHelperText key={index} error={true}>
                                    {msg}
                                </FormHelperText>
                            ))}
                        </>
                    )}
                    {readOnly && choices && (
                        <Box component="div" className={classes.detailNodeValue}>
                            {loading ? Loader : selectedLabels?.map((ele: any) => ele).join(', ')}
                        </Box>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default MultiSelectDropDownFormField;
